import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, ChevronRight, DownloadIcon, FileText,
  Search, Calendar, Info, X, ShieldAlert, CheckCircle2, RotateCcw, AlertTriangle, Lock
} from 'lucide-react';
import { fetchUnduhan, formatTanggalIndonesia } from '../utils/api';

const CAT_OPTIONS = [
  { value: 'Semua', label: 'Semua' },
  { value: 'Formulir', label: 'Formulir' },
  { value: 'Panduan', label: 'Panduan' },
  { value: 'Kalender', label: 'Kalender' },
  { value: 'Laporan_Regulasi', label: 'Laporan & Regulasi' },
  { value: 'akreditasi', label: 'Akreditasi' }
];

const getCatLabel = (val) => {
  const found = CAT_OPTIONS.find(c => c.value === val);
  if (!found && val === 'Laporan & Regulasi') return 'Laporan & Regulasi';
  if (!found && (val === 'Sertifikat' || val === 'sertifikat')) return 'Akreditasi';
  return found ? found.label : (val || 'Umum');
};

const ICON_COLORS = {
  pdf: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-500', label: 'PDF' },
  doc: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-500', label: 'DOCX' },
  docx: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-500', label: 'DOCX' },
  xls: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-500', label: 'XLSX' },
  xlsx: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-500', label: 'XLSX' },
  default: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-500', label: 'FILE' }
};

const getIconMime = (typeStr) => {
  const typ = (typeStr || '').toLowerCase();
  if (typ.includes('pdf')) return 'pdf';
  if (typ.includes('doc')) return 'doc';
  if (typ.includes('xls')) return 'xls';
  return 'default';
};

const Download = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const activeCat = (() => {
    const param = searchParams.get('kategori');
    return CAT_OPTIONS.find(c => c.value === param) ? param : 'Semua';
  })();

  const setActiveCat = (val) => {
    setSearchParams(val === 'Semua' ? {} : { kategori: val }, { replace: true });
  };
  
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Captcha State
  const [showModal, setShowModal] = useState(false);
  const [targetFile, setTargetFile] = useState(null);
  const [captcha, setCaptcha] = useState({ a: 0, b: 0, sum: 0 });
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');

  useEffect(() => {
    const loadFiles = async () => {
      const data = await fetchUnduhan();
      setFiles(data);
      setLoading(false);
    };
    loadFiles();
  }, []);

  const generateCaptcha = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ a, b, sum: a + b });
    setCaptchaInput('');
    setCaptchaError('');
  };

  const handleDownloadClick = (file) => {
    setTargetFile(file);
    generateCaptcha();
    setShowModal(true);
  };

  const processDownload = (e) => {
    e.preventDefault();
    if (parseInt(captchaInput) !== captcha.sum) {
      setCaptchaError('Jawaban salah! Silakan coba lagi.');
      return;
    }

    const downloadUrl = targetFile.berkasUrl || '';

    if (downloadUrl) {
      window.open(downloadUrl, "_blank");
    } else {
       alert("Link download belum tersedia / sedang diproses oleh server.");
    }

    setShowModal(false);
    setTargetFile(null);
  };

  const filtered = files.filter(f => {
    // Tolerasi matching jika data lama memuat text bukan value
    const isMatch = f.kategori === activeCat || 
                   (activeCat === 'Laporan_Regulasi' && f.kategori === 'Laporan & Regulasi') ||
                   (activeCat === 'akreditasi' && (f.kategori?.toLowerCase() === 'akreditasi' || f.kategori?.toLowerCase() === 'sertifikat'));
    
    const matchCat = activeCat === 'Semua' || isMatch;
    const matchSearch = !search.trim() || (f.nama_file && f.nama_file.toLowerCase().includes(search.toLowerCase()));
    return matchCat && matchSearch;
  }).sort((a, b) => {
    // Prioritas pengurutan menurun (terbaru di atas) berbasis tanggal publikasi atau tanggal dibuat
    const dateA = a.tanggal_publikasi || a.date_created || '';
    const dateB = b.tanggal_publikasi || b.date_created || '';
    if (dateA < dateB) return 1;
    if (dateA > dateB) return -1;
    return 0;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCat, search]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedFiles = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div data-testid="download-page" className="relative">
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1"><Home size={14} /> Beranda</Link>
              <ChevronRight size={14} />
              <span className="text-white">Download File</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-4">Download File</h1>
            <p className="text-white/80 max-w-xl text-base">
              Unduh formulir akademik, panduan, kalender, dan dokumen penting STIKES HI Jambi.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { val: CAT_OPTIONS.length - 1, label: 'Kategori' },
              { val: files.length, label: 'Total File' },
              { val: files.filter(f => f.kategori === 'Formulir').length, label: 'Formulir' },
              { val: files.filter(f => f.kategori === 'Panduan').length, label: 'Panduan' },
            ].map((s, i) => (
              <div key={i} className="bg-white dark:bg-[#1A182E] rounded-xl p-4 text-center border border-gray-100 dark:border-gray-800 shadow-sm">
                <div className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading">{loading ? '-' : s.val}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-5">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="download-search"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cari nama file..."
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white dark:bg-[#1A182E] border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE]"
            />
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap mb-8">
            {CAT_OPTIONS.map(cat => (
              <button
                key={cat.value}
                data-testid={`dl-filter-${cat.value.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setActiveCat(cat.value)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCat === cat.value
                    ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/20'
                    : 'bg-white dark:bg-[#1A182E] text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-purple-300'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* File List */}
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            {loading ? 'Memuat data dari CMS...' : `${filtered.length} file ditemukan`}
          </p>

          {loading ? (
             <div className="space-y-3 pb-16">
               {[1,2,3].map(i => (
                 <div key={i} className="bg-white dark:bg-[#1A182E] rounded-xl border border-gray-100 dark:border-gray-800 p-4 h-20 animate-pulse" />
               ))}
             </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Search size={36} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 text-sm">Tidak ada file yang sesuai.</p>
            </div>
          ) : (
            <div className="space-y-3 pb-8">
              {paginatedFiles.map((file, i) => {
                const iconStyle = ICON_COLORS[getIconMime(file.tipe_ekstensi)] || ICON_COLORS.default;
                return (
                  <motion.div
                    key={file.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, delay: i * 0.04 }}
                    className="bg-white dark:bg-[#1A182E] rounded-xl border border-gray-100 dark:border-gray-800 p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-[#7B2FBE] hover:shadow-md transition-all duration-200 group"
                  >
                    <div className={`w-12 h-12 rounded-xl ${iconStyle.bg} ${iconStyle.text} flex items-center justify-center flex-shrink-0 self-start sm:self-auto`}>
                      <FileText size={22} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/download/${file.slug ? file.slug.replace(/\s+/g, '-') : file.id}`}
                        className="block font-semibold text-[#1A1A2E] dark:text-white text-base leading-tight mb-2 truncate group-hover:text-[#7B2FBE] transition-colors hover:underline"
                        onClick={e => e.stopPropagation()}
                      >
                        {file.nama_file}
                      </Link>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className={`font-bold ${iconStyle.text} bg-white dark:bg-[#1A182E] px-2 py-0.5 rounded border border-current/20 uppercase`}>{file.tipe_ekstensi || 'FILE'}</span>
                        {file.ukuran_file && <span>{file.ukuran_file}</span>}
                        {file.tanggal_publikasi && <span className="flex items-center gap-1.5"><Calendar size={12} /> {formatTanggalIndonesia(file.tanggal_publikasi)}</span>}
                        <span className="px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 font-medium">{getCatLabel(file.kategori)}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => handleDownloadClick(file)}
                      className="mt-3 sm:mt-0 flex-shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#7B2FBE]/10 hover:bg-[#7B2FBE] text-[#7B2FBE] hover:text-white font-semibold text-sm transition-all duration-300 w-full sm:w-auto"
                    >
                      <DownloadIcon size={16} /> Unduh
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && filtered.length > 0 && (
            <div className="flex justify-center items-center gap-2 mb-12">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1A182E] text-gray-500 hover:bg-[#7B2FBE]/10 hover:text-[#7B2FBE] disabled:opacity-40 transition-colors"
                title="Halaman Sebelumnya"
              >
                <ChevronRight size={18} className="rotate-180" />
              </button>
              
              <div className="flex gap-1.5 overflow-x-auto px-1 hide-scrollbar">
                {Array.from({ length: totalPages }, (_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPage(idx + 1)}
                    className={`min-w-10 h-10 flex items-center justify-center rounded-xl text-sm font-bold transition-all ${
                      currentPage === idx + 1
                        ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/25 border-transparent'
                        : 'bg-white dark:bg-[#1A182E] border border-gray-200 dark:border-gray-800 text-gray-500 hover:border-purple-300 hover:text-[#7B2FBE]'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1A182E] text-gray-500 hover:bg-[#7B2FBE]/10 hover:text-[#7B2FBE] disabled:opacity-40 transition-colors"
                title="Halaman Selanjutnya"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}

          {/* Info Note */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/40 rounded-xl p-5 flex items-start gap-4">
            <Info size={20} className="text-[#7B2FBE] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Semua file tersedia untuk mahasiswa dan civitas akademika STIKES HI Jambi.
              Jika file yang Anda cari tidak tersedia, silakan hubungi Bagian Akademik atau Tata Usaha STIKES HI Jambi.
            </p>
          </div>
        </div>
      </div>

      {/* Captcha Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowModal(false)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white dark:bg-[#181628] w-full max-w-sm rounded-3xl p-6 relative z-10 shadow-2xl border border-gray-100 dark:border-gray-800"
            >
              <button 
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                type="button"
              >
                <X size={20} />
              </button>

              <div className="w-14 h-14 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-5 mx-auto">
                <Lock size={26} />
              </div>

              <h3 className="text-xl font-bold font-heading text-center text-[#1A1A2E] dark:text-white mb-2">
                Verifikasi Keamanan
              </h3>
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-6 px-4">
                Sistem mendeteksi akses. Jawab persoalan matematika berikut untuk melanjutkan unduhan.
              </p>

              <form onSubmit={processDownload}>
                <div className="bg-gray-50 dark:bg-[#121020] rounded-2xl p-4 border border-gray-100 dark:border-gray-800 mb-4 flex items-center justify-center gap-4">
                  <span className="text-3xl font-bold font-heading text-[#7B2FBE]">{captcha.a}</span>
                  <span className="text-xl font-bold text-gray-400">+</span>
                  <span className="text-3xl font-bold font-heading text-[#7B2FBE]">{captcha.b}</span>
                  <span className="text-xl font-bold text-gray-400">=</span>
                  <input
                    type="number"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    className="w-20 text-center text-2xl font-bold py-2 rounded-xl border-2 border-[#7B2FBE]/30 focus:border-[#7B2FBE] bg-white dark:bg-[#181628] text-[#1A1A2E] dark:text-white outline-none transition-colors"
                    placeholder="?"
                    autoFocus
                  />
                </div>

                {captchaError && (
                  <div className="flex items-center justify-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl mb-4 text-xs font-medium">
                    <AlertTriangle size={14} /> {captchaError}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={generateCaptcha}
                    className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center"
                    title="Ganti Pertanyaan"
                  >
                    <RotateCcw size={18} />
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-[#7B2FBE] hover:bg-[#6A28A5] text-white rounded-xl font-medium text-sm transition-colors py-3.5 flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25"
                  >
                    <CheckCircle2 size={16} /> Verifikasi
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Download;
